import React, { useEffect, useState } from "react";
import {
  Alert,
  Button,
  Box,
  Chip,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import BusinessOutlinedIcon from "@mui/icons-material/BusinessOutlined";
import CalculateOutlinedIcon from "@mui/icons-material/CalculateOutlined";
import BaseDialog from "../../../utils/dialog/BaseDialog";
import { TextFieldForAmount } from "../../../utils/TextField/NumericTextField";

const sumAmounts = (rows) =>
  (rows || []).reduce((total, row) => total + Number(row?.amount || 0), 0);

const formFromCompany = (company) => {
  const saved = company?.externalInput;
  const doc = company?.sourceDoc || {};
  const referenceData = doc.referenceData || {};
  const value = (key, fallback = "") => saved?.[key] ?? fallback;

  return {
    name: value("name", company?.name || ""),
    uen: value("uen", company?.uen || ""),
    companyType: value(
      "companyType",
      doc.company?.companyType === "investment" ? "Investment" : "Trading",
    ),
    adjustedProfit: String(
      value("adjustedProfit", doc.netProfitBeforeTax ?? ""),
    ),
    priorYearCa: String(
      value("priorYearCa", sumAmounts(doc.unutilisedCapitalAllowances)),
    ),
    currentYearCa: String(
      value(
        "currentYearCa",
        sumAmounts(referenceData.currentYearCapitalAllowances),
      ),
    ),
    otherSourcedIncome: String(
      value("otherSourcedIncome", sumAmounts(referenceData.otherSourceIncome)),
    ),
    currentYearLosses: String(value("currentYearLosses", 0)),
    priorYearLosses: String(
      value("priorYearLosses", sumAmounts(doc.unutilisedLosses)),
    ),
    priorYearDonations: String(
      value("priorYearDonations", sumAmounts(doc.unutilisedDonations)),
    ),
    currentYearDonations: String(
      value(
        "currentYearDonations",
        sumAmounts(referenceData.currentYearDonations),
      ),
    ),
    qualifiesForSute: String(value("qualifiesForSute", Boolean(doc.isStartup))),
  };
};

const amountFields = [
  ["adjustedProfit", "Adjusted Profit / (Loss)"],
  ["priorYearCa", "Prior-Year Unutilised CA"],
  ["currentYearCa", "Current-Year CA"],
  ["otherSourcedIncome", "Other-Sourced Income"],
  ["currentYearLosses", "Current-Year Losses"],
  ["priorYearLosses", "Prior-Year Unutilised Losses"],
  ["priorYearDonations", "Prior-Year Unutilised Donations"],
  ["currentYearDonations", "Current-Year Donations"],
];

const sectionSx = {
  p: 2,
  border: 1,
  borderColor: "divider",
  borderRadius: 2,
  bgcolor: "background.paper",
};
const fieldSx = { flex: "1 1 240px", minWidth: 0 };

export default function ExternalCompanyEditDialog({
  open,
  company,
  onClose,
  onSave,
}) {
  const [form, setForm] = useState(() => formFromCompany(company));
  const [error, setError] = useState("");

  useEffect(() => {
    if (!open) return;
    setForm(formFromCompany(company));
    setError("");
  }, [open, company]);

  const update = (key, value) => {
    setError("");
    setForm((current) => {
      const next = { ...current, [key]: value };
      if (key === "companyType" && value === "Investment") {
        next.priorYearCa = "0";
        next.currentYearCa = "0";
        next.currentYearLosses = "0";
        next.priorYearLosses = "0";
        next.priorYearDonations = "0";
      }
      return next;
    });
  };

  const save = () => {
    const result = onSave?.({
      ...form,
      qualifiesForSute: form.qualifiesForSute === "true",
    });
    if (!result?.ok) {
      setError(result?.error || "Unable to update the external company.");
      return;
    }
    onClose?.();
  };

  const investment = form.companyType === "Investment";

  return (
    <BaseDialog
      open={open}
      onClose={onClose}
      title='Edit External Company'
      subtitle='Update the source details used for this Group Relief case.'
      icon={<EditOutlinedIcon />}
      maxWidth='md'
      paperSx={{
        m: { xs: 1, sm: 2 },
        width: "calc(100% - 32px)",
        borderRadius: 3,
        maxHeight: "calc(100dvh - 32px)",
        overflow: "hidden",
      }}
      contentSx={{ p: 2, minHeight: 0 }}
      actionsSx={{
        px: 2,
        py: 1.25,
        gap: 1,
        flexWrap: "wrap",
        "& .MuiButton-root": { textTransform: "none", minHeight: 36 },
      }}
      actions={
        <>
          <Button onClick={onClose}>Cancel</Button>
          <Button variant='contained' disableElevation onClick={save}>
            Save and recalculate
          </Button>
        </>
      }
    >
      <Stack spacing={2}>
        <Stack direction='row' alignItems='center' gap={1} flexWrap='wrap'>
          <Chip label='EXTERNAL' size='small' color='secondary' variant='outlined' />
          <Typography variant='body2' color='text.secondary'>
            Changes apply to this case only. Check the figures again after saving.
          </Typography>
        </Stack>

        {error && <Alert severity='error'>{error}</Alert>}

        <Stack spacing={2} sx={sectionSx}>
          <Stack direction='row' alignItems='center' gap={1.25}>
            <BusinessOutlinedIcon sx={{ color: "action.active" }} />
            <Box>
              <Typography variant='subtitle2' fontWeight={600}>Company details</Typography>
              <Typography variant='caption' color='text.secondary'>
                Identify the company and its tax treatment.
              </Typography>
            </Box>
          </Stack>
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2 }}>
          <Box sx={fieldSx}>
            <TextField
              variant='standard'
              required
              fullWidth
              label='Company name'
              value={form.name}
              onChange={(event) => update("name", event.target.value)}
            />
          </Box>
          <Box sx={fieldSx}>
            <TextField
              variant='standard'
              required
              fullWidth
              label='UEN'
              value={form.uen}
              onChange={(event) => update("uen", event.target.value)}
            />
          </Box>
          <Box sx={fieldSx}>
            <TextField
              variant='standard'
              select
              required
              fullWidth
              label='Company type'
              value={form.companyType}
              onChange={(event) => update("companyType", event.target.value)}
            >
              <MenuItem value='Trading'>Trading</MenuItem>
              <MenuItem value='Investment'>Investment</MenuItem>
            </TextField>
          </Box>
          <Box sx={fieldSx}>
            <TextField
              variant='standard'
              select
              required
              fullWidth
              label='Qualifies for Start-Up Tax Exemption'
              value={form.qualifiesForSute}
              onChange={(event) =>
                update("qualifiesForSute", event.target.value)
              }
            >
              <MenuItem value='true'>Yes</MenuItem>
              <MenuItem value='false'>No</MenuItem>
            </TextField>
          </Box>
          </Box>
        </Stack>

        <Stack spacing={2} sx={sectionSx}>
        <Stack direction='row' alignItems='center' gap={1.25}>
          <CalculateOutlinedIcon sx={{ color: "action.active" }} />
          <Box>
          <Typography variant='subtitle2' fontWeight={600}>
            Tax computation inputs
          </Typography>
          <Typography variant='caption' color='text.secondary'>
            Enter whole-dollar amounts in SGD. Use a negative amount for an
            adjusted loss, or enter Current-Year Losses separately when the
            adjusted profit is zero.
          </Typography>
          </Box>
        </Stack>

        {investment && (
          <Alert severity='info'>
            Capital allowances, current-year losses, prior-year losses and
            prior-year donations are set to zero for an investment company.
          </Alert>
        )}

        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2, pt: 0.5 }}>
          {amountFields.map(([key, label]) => {
            const unavailableForInvestment =
              investment &&
              [
                "priorYearCa",
                "currentYearCa",
                "currentYearLosses",
                "priorYearLosses",
                "priorYearDonations",
              ].includes(key);
            return (
              <Box sx={fieldSx} key={key}>
                <TextFieldForAmount
                  fullWidth
                  label={label}
                  value={form[key]}
                  disabled={unavailableForInvestment}
                  onChange={(value) => update(key, value)}
                />
              </Box>
            );
          })}
        </Box>
        </Stack>
      </Stack>
    </BaseDialog>
  );
}
